/**
 * What has been won, as a grid.
 *
 * The same columns the hand-built Deals table had - who the customer is, how
 * to reach them, where they came from, what they are worth, when they
 * closed and whose they are - with the facts that were captions (plan,
 * cycle, campaign, city, the monthly rate) as columns of their own, hidden
 * until wanted. The page's tabs (To move / Moved to clients / All) decide
 * which rows arrive here; the grid only lays them out.
 *
 * The row's buttons are the old ones: the tick moves a deal to Clients, the
 * arrow undoes a link somebody made, then view, edit and delete.
 */
import { useMemo, useRef } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, formatDate, periodLabel } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Avatar, OwnerPill, Dash, StagePill, EmptyState } from './Primitives.jsx';
import PlanChip from './PlanChip.jsx';
import DataGrid from './grid/DataGrid.jsx';
import { exportAllowed } from './grid/exportGrid.js';
import {
  ContactCell, CompanyCell, SourceCell, EmailCell, ScorePill, aiScoreOf
} from './CrmCells.jsx';
import {
  companyName, clientOfDeal, leadDetails, dealValue, dealsOf
} from '../domain/crm.js';

/** One row: the deal and everything its cells read, worked out once. */
function dealRow(crm, rows, d) {
  const f = leadDetails(crm, d);
  const client = clientOfDeal(rows, d) || null;
  const v = dealValue(rows, d);
  return {
    d,
    f,
    client,
    v,
    company: companyName(crm, d.cid),
    place: [f.city, f.country].filter(Boolean).join(', '),
    close: parseDate(d.close),
    requested: parseDate(f.requested),
    added: parseDate(d.created),
    /* The client record is the billing system, so its plan and cycle win
       over whatever the deal was typed with. */
    plan: (client && client.plan) || d.plan || '',
    cycle: (client && client.cycle) || d.cycle || '',
    score: aiScoreOf(d)
  };
}

/* Free is worth nothing and says so; an amount nobody has filled in is not
   worth nothing, so it is left blank rather than given a figure it has not got. */
const amountOf = v => (v.free ? 0 : v.known ? v.amount : undefined);
const monthlyOf = v => (v.free ? 0 : v.known ? v.monthly : undefined);

const LAYOUT = {
  hidden: [
    'stage', 'plan', 'cycle', 'monthly', 'email', 'phone', 'city', 'country',
    'campaign', 'requested', 'added', 'next'
  ],
  pageSize: 25,
  frozen: 1
};

function dealColumns({ stages, dateFmt, fmtMoney, act, withScores }) {
  const day = d => formatDate(d, false, dateFmt);
  const cols = [
    {
      id: 'title',
      /* Wide enough for the name and a line of chips without wrapping. */
      size: 340,
      minSize: 180,
      accessorFn: r => r.d.title || undefined,
      meta: {
        label: 'Deal',
        type: 'text',
        searchValue: r => `${r.d.title || ''} ${r.client ? r.client.name || '' : ''} ${r.f.products || ''}`
      },
      cell: ({ row: { original: r } }) => (
        <div className="cname">
          <Avatar name={r.d.title} />
          <div style={{ minWidth: 0 }}>
            <b title={r.d.title}>{r.d.title}</b>
            {/* On a row that has become a client, who they became is the
                thing worth the line. */}
            {r.client ? (
              <small><span className="dealwon">now the client {r.client.name}</span></small>
            ) : r.f.products ? (
              <small>Wants {r.f.products}</small>
            ) : null}
            <div className="dealchips">
              {r.client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
              {/* Typed in by hand rather than synced: the reason it may have
                  no tracker detail behind it. */}
              {!r.d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
              <PlanChip list={r.f.productList} label={r.d.plan} />
              {r.d.cycle ? <span className="dealchip">{r.d.cycle}</span> : null}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'contact',
      size: 240,
      accessorFn: r => r.f.email || r.f.phone || undefined,
      meta: {
        label: 'Contact',
        type: 'text',
        exportValue: r => [r.f.email, r.f.phone].filter(Boolean).join(' · ')
      },
      cell: ({ row }) => <ContactCell f={row.original.f} />
    },
    {
      id: 'company',
      size: 210,
      accessorFn: r => r.company || undefined,
      meta: { label: 'Company', type: 'text', searchValue: r => `${r.company} ${r.place}` },
      cell: ({ row }) => <CompanyCell company={row.original.company} place={row.original.place} />
    },
    {
      id: 'source',
      size: 160,
      accessorFn: r => r.d.source || undefined,
      meta: { label: 'Source', type: 'enum', searchValue: r => `${r.d.source || ''} ${r.f.campaign}` },
      cell: ({ row }) => <SourceCell source={row.original.d.source} campaign={row.original.f.campaign} />
    },
    {
      /* The invoice as the client record writes it - 950/yr, not 79/mo -
         because that is the number somebody would go and check. The
         Monthly column is where two cycles are made comparable. */
      id: 'value',
      size: 150,
      accessorFn: r => amountOf(r.v),
      meta: { label: 'Value', type: 'number' },
      cell: ({ row: { original: r } }) => {
        if (r.v.free) return <span className="dealfree">Free</span>;
        if (!r.v.known) return <span title="No amount on the client record"><Dash /></span>;
        return <b>{fmtMoney(r.v.amount)}<span className="dim">{periodLabel(r.v.cycle)}</span></b>;
      }
    },
    {
      id: 'wonOn',
      size: 150,
      accessorFn: r => r.close || undefined,
      meta: { label: 'Won on', type: 'date' },
      cell: ({ row: { original: r } }) => (
        <>
          {r.close ? <span className="datep"><Icon name="cal" />{day(r.close)}</span> : <Dash />}
          {/* Legacy only: a deal typed in at an earlier stage, before the
              page became the won list. */}
          {r.d.stage !== 'Won' ? <div style={{ marginTop: 4 }}><StagePill value={r.d.stage} /></div> : null}
        </>
      )
    },
    {
      id: 'owner',
      size: 150,
      accessorFn: r => r.d.owner || undefined,
      meta: { label: 'Owner', type: 'enum', blank: 'Unowned' },
      cell: ({ getValue }) => <OwnerPill name={getValue()} />
    },
    {
      id: 'stage',
      size: 130,
      accessorFn: r => r.d.stage || undefined,
      meta: { label: 'Stage', type: 'enum', options: stages },
      cell: ({ getValue }) => (getValue() ? <StagePill value={getValue()} /> : <Dash />)
    },
    {
      id: 'plan',
      size: 130,
      accessorFn: r => r.plan || undefined,
      meta: { label: 'Plan', type: 'enum' }
    },
    {
      id: 'cycle',
      size: 120,
      accessorFn: r => r.cycle || undefined,
      meta: { label: 'Cycle', type: 'enum' }
    },
    {
      id: 'monthly',
      size: 130,
      accessorFn: r => monthlyOf(r.v),
      meta: { label: 'Monthly', type: 'number' },
      cell: ({ getValue }) => (getValue() === undefined ? <Dash /> : <b>{fmtMoney(getValue())}<span className="dim">/mo</span></b>)
    },
    {
      id: 'email',
      size: 220,
      accessorFn: r => r.f.email || undefined,
      meta: { label: 'Email', type: 'text' },
      cell: ({ getValue }) => <EmailCell email={getValue()} />
    },
    {
      id: 'phone',
      size: 150,
      accessorFn: r => r.f.phone || undefined,
      meta: { label: 'Phone', type: 'text' }
    },
    {
      id: 'city',
      size: 140,
      accessorFn: r => r.f.city || undefined,
      meta: { label: 'City', type: 'enum' }
    },
    {
      id: 'country',
      size: 170,
      accessorFn: r => r.f.country || undefined,
      meta: { label: 'Country', type: 'enum' }
    },
    {
      id: 'campaign',
      size: 180,
      accessorFn: r => r.f.campaign || undefined,
      meta: { label: 'Campaign', type: 'enum' }
    },
    {
      id: 'requested',
      size: 140,
      accessorFn: r => r.requested || undefined,
      meta: { label: 'Requested', type: 'date' }
    },
    {
      id: 'added',
      size: 140,
      accessorFn: r => r.added || undefined,
      meta: { label: 'Added', type: 'date' }
    },
    {
      id: 'next',
      size: 220,
      accessorFn: r => r.d.next || undefined,
      meta: { label: 'Next step', type: 'text' },
      cell: ({ getValue }) => (getValue() ? <span title={getValue()}>{getValue()}</span> : <Dash />)
    }
  ];

  if (withScores) {
    cols.push({
      id: 'ai',
      size: 110,
      accessorFn: r => r.score,
      meta: { label: 'AI score', type: 'number', search: false },
      cell: ({ row: { original: r } }) => <ScorePill score={r.score} why={r.d.aiWhy} />
    });
  }

  cols.push({
    id: 'actions',
    size: 156,
    enableSorting: false,
    meta: { label: 'Actions', pin: 'right', export: false, filter: false, search: false },
    cell: ({ row: { original: r } }) => (
      <div className="acts">
        {!r.client ? (
          /* Straight to the Clients page: the record is created, the deal
             linked, and the row leaves this tab. */
          <button type="button" className="won" title="Move to clients" onClick={() => act.current.convert(r.d)}>
            <Icon name="check" />
          </button>
        ) : r.d.clientId ? (
          /* A way back, but only from a link somebody made. A match on the
             address is not a tick anybody gave, so there is nothing to undo. */
          <button
            type="button"
            title={`Bring back to Deals — undo the link to ${r.client.name}`}
            onClick={() => act.current.undo(r.d)}
          >
            <Icon name="undo" />
          </button>
        ) : null}
        <button type="button" className="vw" title="View details" onClick={() => act.current.view(r.d)}>
          <Icon name="eye" />
        </button>
        <button type="button" title="Edit" onClick={() => act.current.edit(r.d)}>
          <Icon name="edit" />
        </button>
        <button type="button" className="rm" title="Delete" onClick={() => act.current.remove(r.d)}>
          <Icon name="trash" />
        </button>
      </div>
    )
  });

  return cols;
}

export default function DealsGrid({ list, tab }) {
  const {
    S, crm, rows, setSheet, fmtMoney, me, menus, convertDeal, unconvertDeal, removeFromCrm
  } = useApp();

  const data = useMemo(() => list.map(d => dealRow(crm, rows, d)), [list, crm, rows]);

  /* Through a ref, so the columns are not rebuilt when the store changes. */
  const act = useRef(null);
  act.current = {
    convert: d => convertDeal(d.id),
    undo: d => unconvertDeal(d.id),
    view: d => setSheet({ kind: 'dealView', id: d.id }),
    edit: d => setSheet({ kind: 'deal', id: d.id }),
    remove: d => {
      // eslint-disable-next-line no-alert
      if (window.confirm('Delete this deal?')) removeFromCrm('deals', d.id);
    }
  };

  const withScores = dealsOf(crm).some(d => aiScoreOf(d) !== undefined);
  const stages = S.stages || [];
  const stageKey = stages.join('|');
  const columns = useMemo(
    () => dealColumns({ stages, dateFmt: S.dateFmt, fmtMoney, act, withScores }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageKey, S.dateFmt, fmtMoney, withScores]
  );

  return (
    <DataGrid
      prefKey="grid:deals"
      defaults={LAYOUT}
      columns={columns}
      data={data}
      getRowId={r => r.d.id}
      onRowClick={r => act.current.view(r.d)}
      viewKey={tab}
      label="Deals"
      exportName="xyra-deals"
      exportSheet="Deals"
      canExport={exportAllowed(me, menus)}
      dateFmt={S.dateFmt}
      emptyState={(
        <EmptyState icon="card" tone="g-orange" title="No deals match">
          Clear the filters, win one on the pipeline, or add a deal.
        </EmptyState>
      )}
    />
  );
}
